// ============================================================================
// IS THIS TOOL MEASURED? — ONE ANSWER, FOR EVERY SURFACE THAT ASKS
// ============================================================================
// Ratified 2026-08-13: a tool composes when its VALUES exist. Grades and percentiles govern
// grading artifacts only. That law was ratified because FOUR surfaces each answered this question
// their own way and disagreed on one live report -- and the one that keyed on `grade` named the
// very tools it had just measured as unmeasured.
//
// It happened again on 2026-10-09, in three places at once. The zero-grades law removes the bare
// 20-80 from exactly the tools that ARE measured, so every surface still asking "does it have a
// grade?" inverted its own meaning the moment the grade went:
//
//   * CredibilityFrame (report + PDF) filed every device-measured tool under "Not measurable".
//   * ReportPdfContent's MEASURED TOOLS block emptied, and those tools reappeared under
//     SCOUTING OBSERVATIONS -- a hand-timed pop time presented as somebody's impression.
//   * bbotPdfText did the same to the downloadable PDF.
//
// WHY A MODULE AND NOT A HELPER IN App.js. bbotPdfText.js is deliberately standalone -- the
// customer's PDF and the headless verifier's PDF are drawn by the same code, so a copy of this
// test there would be the thing that drifts. This file imports nothing and holds no React, so
// both can share it without the verifier pulling in a component tree.
//
// THREE WAYS A TOOL SAYS IT WAS MEASURED, and a grade is not one of them:
//
//   1. gradeBasis.value   -- the measured quantity the age reference was read against (4.05 s).
//   2. confidence         -- Confirmed or Measured, i.e. a device or a clock produced it.
//   3. an admin-verified time in measuredValues for this tool, via the mapping the save path
//      itself uses: a Run tool is carried by h2f or the 60, a Pop Time tool by popTime. Ethan
//      Ortiz is why: his hand-timed 4.40 and 2.30 sit in measuredValues with "Verified · admin"
//      on both, while his SECTIONS carry no basis (no bracket was known when they were saved)
//      and a confidence of "Observed"/"Pending". An admin-entered time is ground truth.
//
// Anything else is not measured, which is the safe direction to fail: a tool wrongly called
// unmeasured loses a badge, and a tool wrongly called measured is a claim BBOT cannot support.

// The timed value that carries each tool. Mirrors the TKEY map in saveMeasuredTime -- if a tool
// gains a timed source, both change together or the save writes a value no surface credits.
var TIMED_FOR_TOOL = {
  run: ["h2f", "dash60"],
  h2f: ["h2f"],
  dash60: ["dash60"],
  poptime: ["popTime"],
  pop: ["popTime"],
};

// "Verified" is tested the same way the credibility frame tests it for its own source count:
// the basis string STARTS with "verified". A substring test would match a sentence that merely
// mentions the word.
function basisIsVerified(s) {
  return String(s || "").toLowerCase().indexOf("verified") === 0;
}

export function toolIsMeasured(section, measuredValuesBasis) {
  if (!section) return false;
  var gb = section.gradeBasis;
  if (gb && gb.value != null) return true;
  var c = String(section.confidence || "").toLowerCase();
  if (c.indexOf("confirmed") >= 0 || c.indexOf("measured") >= 0) return true;
  var mvb = measuredValuesBasis || {};
  var keys = TIMED_FOR_TOOL[String(section.key || "").toLowerCase()] || [];
  for (var i = 0; i < keys.length; i++) {
    if (basisIsVerified(mvb[keys[i]])) return true;
  }
  return false;
}

// ============================================================================
// THE AGE-PEER PERCENTILE IS NOT RENDERED. THERE IS NO FLAG.
// ============================================================================
// RULED BY LUIS, 2026-10-09: *"a percentile from a fixed curve in code is a fabricated number, and
// it reached customer PDFs ... The record doesn't keep two paths alive. The render is removed; the
// write stays in its column; the only thing that can turn a placement back on is Part 5b clearing
// n for a real pool, built then, not flipped. Delete the flag."*
//
// WHAT THE NUMBER WAS. `gradeBasis.percentile` is read off a STATIC CURVE written into
// BbotBoxReport.js -- `_TIMED_CFG.dials`: an anchor, a multiplier per age bracket, a fixed spread,
// interpolated. No BBOT player, rep or session is in it. Printed beside a player's name it reads
// as "this is where he sits among his peers", and there are no peers in it. It rendered on the
// customer PDF, ungated, until this change.
//
// WHY NO FLAG, which was my first answer and was wrong: a disabled render is a second path kept
// alive, and the one edit that flips it back carries none of the reasoning that took it out. A
// real placement is a different quantity -- counted against a named pool, with n stated -- and
// when the pool exists it gets BUILT, against that pool, not revived by flipping a boolean that
// still points at the curve.
//
// The write stays: `gradeBasis` remains on the row and in its column, because the VALUE and the
// bracket in it are real and the record keeps what it computed.
//
// Nothing in this module exports a render switch. If you are here looking for one, the answer is
// in the paragraph above.
